import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";

// Hardcoded fallbacks — kept in sync with backend PAGE_CONTENT_DEFAULTS.
const SHARED = { kicker_color: null, title_color: null };
const DEFAULTS = {
  home_featured: {
    kicker: "From the editor",
    title: "The latest from LAST.",
    intro: "Written by fans, for fans. Tap any post to read.",
    ...SHARED,
  },
  home_explore: {
    kicker: "Explore",
    title: "Three lanes, one Sky Blue hub.",
    intro: "",
    ...SHARED,
  },
  news: {
    kicker: "Newsroom",
    title: "Everything Coventry, in one scroll.",
    intro:
      "Aggregated from BBC Sport, CoventryLive, Sky Sports, the Guardian and the official club site. Tap a source to filter.",
    ...SHARED,
  },
  videos: {
    kicker: "Broadcast",
    title: "Latest from the Sky Blue creators.",
    intro:
      "Auto-pulled from official and fan YouTube channels. Tap play to watch inline — no leaving the page.",
    ...SHARED,
  },
  stats: {
    kicker: "Match centre",
    title: "Table, fixtures & results.",
    intro:
      "Championship standings and the Sky Blues' next five plus last five matches.",
    ...SHARED,
  },
  retro: {
    kicker: "Retro",
    title: "The archive,\nsky-blue and proud.",
    intro:
      "Memories, match-day snapshots, and the stories that made the Sky Blues who they are. Written and curated by LAST.",
    ...SHARED,
  },
  spotify: {
    kicker: "Soundtrack",
    title: "The Sky Blue playlist.",
    intro:
      "Matchday songs, fan chants and a bit of everything in between. Press play and turn it up.",
    ...SHARED,
  },
  social: {
    kicker: "PUSB wall",
    title: "#PUSB — straight from the lads.",
    intro:
      "Hand-picked posts from Coventry City players and the Sky Blues faithful. Posts stay on their original platform — we just point you to them.",
    ...SHARED,
  },
  links: {
    kicker: "Useful links",
    title: "The Sky Blue directory.",
    intro:
      "Supporters' clubs, local businesses and other sites the Sky Blue faithful should know about. Curated by LAST.",
    ...SHARED,
  },
  contact: {
    kicker: "Get in touch",
    title: "Say hello, send a tip,\ndrop some feedback.",
    intro:
      "Messages drop straight into the LAST inbox. Keep it civil — this is a one-person operation. Expect a reply within a few days.",
    ...SHARED,
  },
  lineup_builder: {
    kicker: "LAST Interactive",
    title: "Coventry City Lineup Builder",
    intro:
      "Select formation, assign players, and drag shirts freely to design your perfect CCFC Matchday 11.",
    ...SHARED,
  },
  classic_lineup_builder: {
    kicker: "LAST Interactive",
    title: "Classic Coventry City Lineup Builder",
    intro:
      "Select formation, assign legends, and drag shirts freely to build your ultimate Classic Coventry City XI.",
    ...SHARED,
  },
  splash: {
    kicker: "Coming soon",
    title: "Something sky blue\nis on the way.",
    intro:
      "LAST is an independent hub for Coventry City supporters — news, videos, the match centre, and a curated #PUSB wall. We're putting the finishing touches to the first release. Check back soon.",
    ...SHARED,
  },
  archive: {
    kicker: "Archive",
    title: "Older posts.",
    intro:
      "Posts that have moved on from the front page. Use the search box to dig something specific up.",
    ...SHARED,
  },
};

const PageContentContext = createContext({ content: DEFAULTS, refresh: () => {} });

// Cache key — bump suffix if the shape of cached entries ever changes.
const CACHE_KEY = "last:page-content:v2";

// Read the last known good API response from localStorage so returning
// visitors see their saved content on first paint (no flash of stale
// defaults). Safe in SSR / private-mode browsers — falls back to {} on
// any error.
const readCachedContent = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
};

const writeCachedContent = (data) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (_) {
    /* quota / private mode — non-fatal */
  }
};

export const PageContentProvider = ({ children }) => {
  // Seed initial state from localStorage if available, otherwise hardcoded defaults.
  // The merge preserves any keys present in DEFAULTS but missing from cache (e.g.
  // a new page added in code before the next API refresh ships).
  const [content, setContent] = useState(() => {
    const cached = readCachedContent();
    if (!cached) return DEFAULTS;
    const merged = { ...DEFAULTS };
    Object.keys(cached).forEach((k) => {
      merged[k] = { ...(DEFAULTS[k] || {}), ...cached[k] };
    });
    return merged;
  });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/page-content");
      setContent((prev) => {
        const merged = { ...prev };
        Object.keys(r.data).forEach((k) => {
          merged[k] = { ...(prev[k] || {}), ...r.data[k] };
        });
        return merged;
      });
      writeCachedContent(r.data);
    } catch (_) {
      /* keep cached / defaults */
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ content, refresh }), [content, refresh]);

  return (
    <PageContentContext.Provider value={value}>
      {children}
    </PageContentContext.Provider>
  );
};

export const usePageContent = (pageId) => {
  const { content } = useContext(PageContentContext);
  return content[pageId] || DEFAULTS[pageId] || { kicker: "", title: "", intro: "", kicker_color: null, title_color: null };
};

export const useRefreshPageContent = () => {
  return useContext(PageContentContext).refresh;
};

// Helpers for sections — return an inline style object only when an override is present.
export const kickerStyle = (copy) => (copy?.kicker_color ? { color: copy.kicker_color } : undefined);
export const titleStyle = (copy) => (copy?.title_color ? { color: copy.title_color } : undefined);
